import { useMemo } from 'react'
import type { AsyncState } from '../../platform/async/Async'
import { useAccounts } from '../accounts/useAccounts'
import { buildTree, CATEGORY_TYPE_LABEL } from '../categories/categoryTree'
import { useCategories } from '../categories/useCategories'
import { useHouseholdId } from '../household/useHousehold'
import { useMembers } from '../household/useMembers'
import { accountLabel } from './txnDisplay'
import { useTransactionTags } from './useTransactions'

/** Where one list stands. The transactions bar ignores it; a dialog that makes a claim about the list reads it. */
export type ListState = { status: 'loading' | 'ready' } | { status: 'error'; retry: () => void }

export type FilterOptions = {
  accounts: { id: string; label: string }[]
  /** Flat, under a heading per category type — no parent/child nesting, so every option filters by exactly its own id. */
  categories: { group: string; items: { id: string; label: string }[] }[]
  /** Household members, by user id: what `beneficial_owner_id` takes. There is no "Household" entry (Q8). */
  owners: { id: string; label: string }[]
  tags: string[]
  /** Per-list readiness, so a consumer can tell "empty" from "not loaded" and "failed". */
  lists: { accounts: ListState; owners: ListState; tags: ListState }
}

function listState(s: AsyncState<unknown>): ListState {
  if (s.status === 'loading') return { status: 'loading' }
  if (s.status === 'error') return { status: 'error', retry: s.recovery?.onAct ?? (() => {}) }
  return { status: 'ready' }
}

/**
 * The lists the filter controls choose from. A list that has not loaded, or
 * failed to, is an empty one: the control then offers only its "All" entry,
 * which is the honest degradation — a filter row is no place for an error panel
 * and the list itself carries its own states.
 */
export function useFilterOptions(): FilterOptions {
  const accounts = useAccounts()
  const categories = useCategories()
  const household = useHouseholdId()
  const members = useMembers(household.id)
  const tags = useTransactionTags()

  return useMemo<FilterOptions>(
    () => ({
      accounts:
        accounts.status === 'success' || accounts.status === 'stale'
          ? accounts.data.map((a) => ({ id: a.id, label: accountLabel(a) }))
          : [],
      categories:
        categories.status === 'success'
          ? buildTree(categories.data.filter((c) => c.is_active)).map((segment) => ({
              group: CATEGORY_TYPE_LABEL[segment.type],
              items: segment.nodes.flatMap((n) =>
                [n.category, ...n.children].map((c) => ({ id: c.id, label: c.name })),
              ),
            }))
          : [],
      owners:
        members.status === 'success'
          ? members.data.map((m) => ({ id: m.userId, label: m.displayName ?? m.email ?? 'Member' }))
          : [],
      tags: tags.status === 'success' ? tags.data : [],
      lists: { accounts: listState(accounts), owners: listState(members), tags: listState(tags) },
    }),
    [accounts, categories, members, tags],
  )
}
